import React, { useCallback, useRef, useState } from "react";
import AppLayout from "@/Layouts/AppLayout";
import OrganizationLayout from "@/Layouts/OrganizationLayout";
import { Tab, Tabs } from "@/Components/Tabs";
import LayoutIcon from "@/Components/LayoutIcon";
import { Cogwheel as CogwheelIcon } from "@/Icons";
import { InertiaLink, usePage } from "@inertiajs/inertia-react";
import { FormError, Formheader, GrayBox } from "@/Components";
import { LabeledInput } from "@/Components/Form";
import { FileText, Upload, XCircle } from "react-feather";
import FileUpload from "@/Components/FileUpload";
import { Inertia } from "@inertiajs/inertia";
import { toast } from "react-toastify";
import { showToast } from "@/Utilities/Utilities";

export default function CompanySettings({ associated_files }) {
  const organization = usePage().props?.organization?.data || null;
  const users = usePage().props?.users?.data || [];
  const errors = usePage().props?.errors || {};

  const defaultState = {
    name: organization.name || "",
    organization_number: organization.organization_number || "",
    email: organization.email || "",
    phone: organization.phone || "",
  };

  const defaultLogoState = organization?.logo || null;

  const [payload, setPayload] = useState(defaultState);
  const [previewImage, setPreviewImage] = useState(defaultLogoState);
  const [previewImageError, setPreviewImageError] = useState("");

  const onFormChange = (e) => {
    setPayload((prev) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
  };

  const handlePreviewImage = (e) => {
    setPreviewImageError("");

    const file = e.target.files[0];

    if (file && file["type"].split("/")[0] === "image") {
      // Only allow images to be uploaded
      setPayload((prev) => ({ ...prev, logo_file: file }));
      setPreviewImage(URL.createObjectURL(file));
    } else {
      setPreviewImageError(
        "The selected file type is not allowed. Only jpg, png, svg or webp can be used",
      );
    }
  };

  function handleRemoveImage() {
    setPayload((prev) => ({ ...prev, logo_file: null }));
    setPreviewImage(null);
  }

  const settingsFormRef = useRef(null);

  const isDisabled = useCallback(
    () => settingsFormRef.current?.checkValidity() !== true,
    [settingsFormRef],
  );

  const onSubmit = (e) => {
    e.preventDefault();

    const options = {};
    if (payload.logo_file !== false) {
      if (payload.logo_file) {
        // An image has been uploaded, so we need to send the file as a FormData object
        options.forceFormData = true;
      }
    }

    const id = toast.loading("Please wait...");

    Inertia.post(route("agency.settings.update"), payload, {
      ...options,
      onSuccess: () => {
        showToast(id, "The company settings were successfully updated!");
      },
      onError: () => {
        showToast(
          id,
          "An error occurred while updating the company settings",
          "error",
        );
      },
    });
  };

  return (
    <Tabs items={["Company details", "Billing info"]}>
      {/*"Billing info"*/}
      <Tab>
        <form
          ref={settingsFormRef}
          className="flex flex-col gap-4"
          onSubmit={onSubmit}
        >
          <GrayBox margin="mb-4">
            <div className="flex flex-wrap gap-4">
              <Formheader margin="w-full">Company details</Formheader>
              <LabeledInput
                required
                title="Name of organization"
                name="name"
                onChange={onFormChange}
                containerClass="w-full sm:basis-1/2-gap-4"
                placeholder="Enter name of organization"
                value={payload?.name || ""}
                error={errors?.name}
              />
              <LabeledInput
                required
                title="Organization number"
                name="organization_number"
                onChange={onFormChange}
                containerClass="w-full sm:basis-1/2-gap-4"
                placeholder="Enter organization number"
                value={payload?.organization_number || ""}
                error={errors?.organization_number}
              />
              <LabeledInput
                required
                title="Contact e-mail"
                name="email"
                type="email"
                onChange={onFormChange}
                containerClass="w-full"
                placeholder="Enter contact e-mail"
                value={payload?.email || ""}
                error={errors?.email}
              />
              <LabeledInput
                required
                title="Contact phone number"
                name="phone"
                onChange={onFormChange}
                containerClass="w-full"
                placeholder="Enter contact phone number"
                value={payload?.phone || ""}
                error={errors?.phone}
              />

              <div className="w-full">
                <span className="mb-1 block text-[14px]">Logotype</span>
                {previewImage === null && (
                  <FileUpload onFileAdded={handlePreviewImage} />
                )}
                {previewImage && (
                  <div className="relative mt-2 flex w-full items-center justify-center gap-2 border border-borderColor bg-gray p-8">
                    <img
                      alt="Logo"
                      src={previewImage}
                      className="w-full max-w-[200px] object-scale-down"
                    />
                    <button
                      onClick={() => handleRemoveImage()}
                      title="Remove image"
                    >
                      <XCircle
                        className="absolute right-2 top-2 scale-100 cursor-pointer p-2 transition-transform hover:scale-110"
                        size={42}
                      />
                    </button>
                  </div>
                )}
                <FormError error={errors.logo_file || previewImageError} />
              </div>
            </div>
          </GrayBox>
          <div className="flex w-full items-center justify-end gap-4">
            <button
              className="cursor-pointer text-blue underline hover:no-underline"
              onClick={() => {
                setPayload(defaultState);
                setPreviewImage(defaultLogoState);
              }}
              type="button"
            >
              Cancel
            </button>
            <button className="btn-blue grow-0" disabled={isDisabled()}>
              Save changes
            </button>
          </div>
        </form>
      </Tab>
      <Tab>
        <GrayBox margin="mb-4">
          <Formheader margin="w-full">Current plan</Formheader>
          <h2 className="mb-4 mt-8 font-rozhaone text-5xl">
            Emma commercial license
          </h2>
          <p className="mb-2 flex w-full max-w-none justify-between gap-4 border-b border-b-gray pb-2">
            <span>Baseline license including reporting modules </span>
            <span className="font-bold">
              {organization.billing.pricing.monthly_price || "-"} SEK
            </span>
          </p>
          <p className="mb-2 flex w-full max-w-none justify-between gap-4 border-b border-b-gray pb-2">
            <span>
              {users.length} global reporters,{" "}
              {organization.billing.pricing.global_reporter_price} SEK each
            </span>

            <span className="font-bold">
              {users.length *
                organization.billing.pricing.global_reporter_price}{" "}
              SEK
            </span>
          </p>
          <h3 className="mb-4 flex justify-between gap-4 font-rozhaone text-xl">
            <span>Monthly license</span>
            <span className="font-bold">
              {Number(organization.billing.pricing.monthly_price || 0) +
                users.length *
                  Number(
                    organization.billing.pricing.global_reporter_price || 0,
                  )}{" "}
              SEK
            </span>
          </h3>

          <p>
            Please review your service agreement and framework agreement for
            more information.
          </p>
          <Formheader margin="w-full py-8 mt-8 border-t border-gray-inactive">
            Global reporters
          </Formheader>
          <div className="mb-8 flex flex-col gap-2">
            {users.map((user, index) => (
              <div
                key={index}
                className=" flex w-full items-center justify-between gap-4 bg-white p-4"
              >
                <div className="flex items-center gap-2">
                  <span>{user.fullname}</span>
                </div>
                <span className="font-bold">
                  {organization.billing.pricing.global_reporter_price || "-"}{" "}
                  kr/month
                </span>
              </div>
            ))}
          </div>
          <InertiaLink
            href={route("agency.users")}
            className="underline hover:no-underline"
          >
            Manage users
          </InertiaLink>
        </GrayBox>
        {associated_files?.length > 0 && (
          <GrayBox>
            <Formheader margin="w-full mb-8">Files</Formheader>
            <div className="flex w-full flex-col gap-2">
              {associated_files.map((file, index) => (
                <div
                  key={index}
                  className="flex w-full items-center justify-between gap-4 bg-white p-4"
                >
                  <div className="flex items-center gap-2">
                    <FileText />
                    <span>{file.name}</span>
                  </div>
                  <a
                    href={file.url}
                    download
                    target="_blank"
                    className="underline hover:no-underline"
                  >
                    Download
                  </a>
                </div>
              ))}
            </div>
          </GrayBox>
        )}
      </Tab>
    </Tabs>
  );
}

CompanySettings.layout = (page) => (
  <AppLayout title="Company settings">
    <OrganizationLayout
      header="Company settings"
      icon={
        <LayoutIcon>
          <CogwheelIcon size={40} className="text-black" />
        </LayoutIcon>
      }
    >
      {page}
    </OrganizationLayout>
  </AppLayout>
);
